import { isPlatformBrowser } from "@angular/common";
import {
  afterNextRender,
  Component,
  DestroyRef,
  inject,
  Injector,
  OnInit,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import {
  DayAheadWidgetComponent,
  EnergyType,
  PriceItemDto,
  ViewType,
  VolumeEnum,
  WidgetApiPayload,
} from "enwyse-widgets";
import { EnergyPriceService } from "../../services/energy-price.service";

const DEFAULT_LANG = "nl";
/** Languages that actually have a translation file under src/assets/i18n. Anything
 *  else falls back to Dutch, so the widget can never mix Dutch labels with dates
 *  rendered in another language. */
const SUPPORTED_LANGS = ["nl", "fr"];

@Component({
  selector: "app-day-ahead-embed",
  standalone: true,
  imports: [DayAheadWidgetComponent],
  template: `
    @if (showWidget()) {
      <day-ahead-widget
        [initialDate]="initialDate"
        [latestAvailableDate]="latestAvailableDate"
        [rawData]="rawData()"
        [lastUpdated]="lastUpdated()"
        (dateChange)="onDateChange($event)"
        ngSkipHydration
      >
      </day-ahead-widget>
    }
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        height: 100%;
      }
    `,
  ],
})
export class DayAheadEmbedComponent implements OnInit {
  private readonly energyPriceService = inject(EnergyPriceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);

  readonly showWidget = signal(false);

  initialDate: DateTime = DateTime.now().startOf("day");
  latestAvailableDate: DateTime | undefined;
  rawData: WritableSignal<PriceItemDto[]> = signal([]);
  lastUpdated: WritableSignal<DateTime | undefined> = signal(undefined);

  constructor() {
    afterNextRender(
      () => {
        this.showWidget.set(true);
        void this.initializeIframeResizer();
      },
      { injector: this.injector },
    );
  }

  ngOnInit(): void {
    const requestedLang = (this.route.snapshot.queryParamMap.get("lang") ?? DEFAULT_LANG).toLowerCase();
    const lang = SUPPORTED_LANGS.includes(requestedLang) ? requestedLang : DEFAULT_LANG;
    this.translate.use(lang);

    this.loadInitialDayAheadDate();
  }

  private loadInitialDayAheadDate(): void {
    const requestedDate = DateTime.utc().startOf('day');

    this.energyPriceService.getFirstAvailableEnergyPriceDate({
      requestedDate: requestedDate.toISO()!,
      energyType: 'electric',
      viewType: 'dayHour',
      volume: 'mWh',
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (res?.availableDate) {
          this.latestAvailableDate = DateTime.fromISO(res.availableDate);
          return;
        }
      },
      error: () => {},
    });
  }

  onDateChange(payload: WidgetApiPayload): void {
    this.energyPriceService
      .getEnergyPrices({
        startDate: payload.startDate.toISOString(),
        endDate: payload.endDate.toISOString(),
        energyType: this.toEnergyTypeString(payload.energyType!),
        viewType: this.toViewTypeString(payload.viewType),
        volume: payload.volume === VolumeEnum.KWh ? "kWh" : "mWh",
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          if (!res?.data) {
            this.rawData.set([]);
            return;
          }
          this.rawData.set(
            res.data.map(
              (item: any) =>
                ({
                  date: new Date(item.date),
                  price: item.price,
                }) as PriceItemDto,
            ),
          );
          if (payload.energyType === EnergyType.Imbalance && res.lastUpdated) {
            this.lastUpdated.set(DateTime.fromISO(res.lastUpdated));
          }
        },
        error: () => this.rawData.set([]),
      });
  }

  private toEnergyTypeString(type: EnergyType): string {
    switch (type) {
      case EnergyType.Electric:
        return "electric";
      case EnergyType.Gas:
        return "gas";
      case EnergyType.Imbalance:
        return "imbalance";
      default:
        return "electric";
    }
  }

  private toViewTypeString(type: ViewType): string {
    switch (type) {
      case ViewType.Day:
        return "day";
      case ViewType.DayHour:
        return "dayHour";
      case ViewType.Week:
        return "week";
      case ViewType.Month:
        return "month";
      case ViewType.Year:
        return "year";
      default:
        return "day";
    }
  }

  private async initializeIframeResizer(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    await import("@iframe-resizer/child");
  }
}
